import type { ErrorEvent } from '@sentry/core/build/types/types-hoist/event';
import { isGetStateWhileReducingError, isKilledStoreError } from '../../store/middleware/DispatchWatchdog';
import { isStaleChunkReloadInProgress } from './StaleChunkReload';

/** Our code ships as Vite chunks under /assets/; anything else on a stack is the page, the browser or an injected script. */
function isOwnFrame(frame: { filename?: string }): boolean {
  return /\/assets\/[^/]+\.js/.test(frame.filename ?? '');
}

/**
 * Browser extensions' own URLs, MetaMask's in-page provider, Check Point's script host, and script
 * files served from the page's own origin that are not our build. We serve no script outside /assets/,
 * so such a file is one an extension injected into the page, like Check Point Harmony's `/2/zp.js`
 * (SHOP-WEB-3B). Harmony also loads the same script from its own host
 * (`https://zerophishing.iaas.checkpoint.com/1/zp.js`), which the same-origin test cannot see.
 */
function isInjectedScriptFrame(frame: { filename?: string }): boolean {
  const filename = frame.filename ?? '';

  if (
    /^(chrome|moz|safari(-web)?)-extension:\/\/|\/scripts\/inpage\.js|^https:\/\/[^/]+\.checkpoint\.com\//.test(
      filename,
    )
  ) {
    return true;
  }

  if (isOwnFrame(frame) || !/\.m?js([?#]|$)/.test(filename)) {
    return false;
  }

  try {
    return new URL(filename, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Sentry's own wrapper around `setTimeout`, `requestAnimationFrame`, event listeners and the like.
 * It is minified into our bundle, so the outermost frame of such an event is "ours" even when the
 * callback it ran came from an injected script.
 */
const SENTRY_WRAPPER_MECHANISM = /^auto\.browser\.browserapierrors\./;

/** Every frame, past Sentry's own wrapper, belongs to an extension or a script it injected. */
function isInjectedStack(frames: { filename?: string }[], mechanism = ''): boolean {
  // Frames run outermost first, so the SDK wrapper is the first one.
  const callerFrames = SENTRY_WRAPPER_MECHANISM.test(mechanism) ? frames.slice(1) : frames;

  return callerFrames.length > 0 && callerFrames.every(isInjectedScriptFrame);
}

/**
 * Messages only ever raised by code that is not ours: MetaMask, the Outlook / Bing link scanner's
 * injected RPC rejecting, and legacy browsers missing `globalThis` or tripping over `Node.contains`.
 */
const FOREIGN_ERROR_MESSAGE =
  /MetaMask|Object Not Found Matching Id:\d+, MethodName:update, ParamCount:4|^globalThis is not defined$|^Argument 1 \('other'\) to Node\.contains must be an instance of Node$/;

/** Stripe.js, served from js.stripe.com only (any release train: /v3/, /basil/, /endive/, …). */
function isStripeJsFrame(frame: { filename?: string }): boolean {
  return /^https:\/\/js\.stripe\.com\//.test(frame.filename ?? '');
}

/** The browser's own code: no file to point at. WebKit labels it `[native code]`, V8 `<anonymous>`. */
function isNativeFrame(frame: { filename?: string }): boolean {
  return frame.filename === '[native code]' || frame.filename === '<anonymous>';
}

/**
 * Synthetic monitoring agents. WebPageTest (`PTST/`) runs our menus on throttled lab devices and
 * aborts what is still loading when its run ends (SHOP-WEB-4W: ten "signal is aborted without
 * reason" in two days, every one from it, none from a visitor). No customer is behind them.
 */
const SYNTHETIC_AGENT = /\bPTST\/\d/;

/**
 * A real infinite recursion repeats the same call sites of ours all the way down the stack
 * (SHOP-WEB-5B: setupModifier → modifier_groups → setupModifier on a menu whose modifier groups
 * reference each other). A call site seen three times is that loop; an overflow without one ran
 * out of stack somewhere that is not our recursion.
 */
function isRecursionInOurCode(frames: { filename?: string; lineno?: number; colno?: number }[]): boolean {
  const seen = new Map<string, number>();

  return frames.filter(isOwnFrame).some((frame) => {
    const site = `${frame.filename}:${frame.lineno}:${frame.colno}`;
    const count = (seen.get(site) ?? 0) + 1;
    seen.set(site, count);

    return count >= 3;
  });
}

/** A frame on the HTML document itself (an inline or injected script), not on any script file. */
function isInlinePageFrame(frame: { filename?: string }): boolean {
  const filename = frame.filename ?? '';

  return /^(https?:\/\/|\/)/.test(filename) && !/\.m?js([?#]|$)/.test(filename);
}

/**
 * Sentry `beforeSend`. Drops events we have established are noise — third-party or
 * browser-injected failures our code neither causes nor can act on — and lets everything
 * else through. Each rule stays as narrow as the evidence behind it, so a real bug that
 * happens to share a message still reaches Sentry.
 */
export default function beforeSendError(event: ErrorEvent): ErrorEvent | null {
  // The page is reloading onto a new build after a stale chunk (see StaleChunkReload). Whatever the
  // half-resolved import throws in the meantime is a side effect of that recovery, already logged.
  if (isStaleChunkReloadInProgress()) {
    return null;
  }

  // Crawlers terminate a page's script on their own time budget, which can land mid-reducer and
  // leave the store stuck (see DispatchWatchdog). HeadlessChrome sent nothing but Redux #3 over
  // 14 days (SHOP-WEB-E / -W / -2N); real Firefox visitors with the same error still come through.
  if (
    /HeadlessChrome/.test(navigator.userAgent) &&
    event?.exception?.values?.some((value) => isGetStateWhileReducingError(value.value))
  ) {
    return null;
  }

  // Firefox cancelled the page's JS mid-reducer because the visitor is navigating away (see
  // DispatchWatchdog): the store refuses every call from then on, on a page that is being left.
  if (event?.exception?.values?.some((value) => isKilledStoreError(value.value))) {
    return null;
  }

  if (SYNTHETIC_AGENT.test(navigator.userAgent)) {
    return null;
  }

  // React's scheduler re-entered mid-render. Firefox runs queued tasks while a blocking alert, sync
  // XHR or its own browser UI holds the page (facebook/react#17355); the stack is React's alone
  // (STOREFRONT-WEB-2J, Firefox Mobile). Other browsers still report it.
  if (
    /Firefox\//.test(navigator.userAgent) &&
    event?.exception?.values?.some((value) => value.value === 'Should not already be working.')
  ) {
    return null;
  }

  // A page without a <head> is one a scraper has already torn down while our scripts still run:
  // Helmet and react-style-singleton then fail on the missing head (SHOP-WEB-49 / -4A / -4B, one
  // HeadlessChrome run over eight menus, never any other browser). No browser shows such a page.
  if (!document.head) {
    return null;
  }

  // The Obscura headless browser (a scraping runtime) runs our page from its own bootstrap script,
  // which it names `<obscura:bootstrap>`, a URL no browser gives a script. SHOP-WEB-5S: styled-components
  // error #17, a <style> with no CSSOM sheet, which no real browser hands out. The bootstrap frame sits
  // under the error boundary's cause, so every exception value is looked at.
  if (
    event?.exception?.values?.some((value) =>
      value.stacktrace?.frames?.some((frame) => /^<obscura:/.test(frame.filename ?? '')),
    )
  ) {
    return null;
  }

  // filter out UnhandledRejection errors that have no information
  if (
    event !== undefined &&
    event.exception !== undefined &&
    event.exception.values !== undefined &&
    event.exception.values.length == 1
  ) {
    const e = event.exception.values[0];

    if (e.type === 'UnhandledRejection' && e.value.includes('Non-Error promise rejection captured with value:')) {
      return null;
    }

    // Only drop the generic non-Error rejection wrapper (Safari reports a rejected bare event as
    // `CustomEvent`). Typed errors (TypeError, ...) rejected inside promises are real bugs and must
    // reach Sentry.
    if (
      ['UnhandledRejection', 'CustomEvent', 'Event'].includes(e.type) &&
      e.value?.includes('captured as promise rejection')
    ) {
      return null;
    }

    const frames = e.stacktrace?.frames ?? [];

    // The Instagram / Facebook Android in-app browser's own performance logger (`iabjs://`)
    // posting to a WebView bridge that is already torn down.
    if (frames.some((frame) => frame.filename?.startsWith('iabjs://'))) {
      return null;
    }

    // Adyen's Apple Pay service calls abort() on a session the customer already dismissed, which
    // WebKit rejects. The customer cancelled; the checkout itself is unaffected. beforeSend sees
    // minified frames, so the native `abort` frame is what identifies it.
    if (e.type === 'InvalidAccessError' && frames.some((frame) => frame.function === 'abort')) {
      return null;
    }

    // Same dismissed session, other path (SHOP-WEB-2X): Adyen's completePayment() on it throws, and
    // Adyen's own catch hands that exception back to completePayment(), which rejects it for having
    // no `status`. The order is settled by our checkout, not by the sheet.
    if (
      e.type === 'TypeError' &&
      /ApplePayPaymentAuthorizationResult\.status is required/.test(e.value ?? '') &&
      frames.some((frame) => frame.function === 'completePayment')
    ) {
      return null;
    }

    // Stripe.js's own listener running on an Element React already destroyed, when an iOS tab left on
    // another page comes back (SHOP-WEB-51: four at once on /menu, the guest then paid normally). Every
    // frame past Sentry's listener wrapper is Stripe.js; a call of ours on a destroyed Element would
    // have our bundle on the stack and still report.
    if (
      e.type === 'IntegrationError' &&
      /^This Element has already been destroyed/.test(e.value ?? '') &&
      SENTRY_WRAPPER_MECHANISM.test(e.mechanism?.type ?? '') &&
      frames.length > 1 &&
      frames.slice(1).every((frame) => isStripeJsFrame(frame) || isNativeFrame(frame))
    ) {
      return null;
    }

    // Apple's Apple Pay SDK (applepay.cdn-apple.com, loaded by Adyen) giving its <apple-pay-button> a
    // second shadow root when a Safari extension re-inserts the button (SHOP-WEB-4Z: the insert comes
    // from `webkit-masked-url://`, Safari's mask for extension code). Nothing of ours on the stack.
    if (
      e.type === 'NotSupportedError' &&
      frames.some((frame) => /\/jsapi\/[^/]+\/apple-pay-sdk\.js/.test(frame.filename ?? '')) &&
      frames.some((frame) => frame.function === 'attachShadow') &&
      !frames.some(isOwnFrame)
    ) {
      return null;
    }

    // The Google iOS app's (GSA) own WKWebView bridge: a reply-handler message its injected script
    // posts, rejected when the app closes the web view (SHOP-WEB-4Y). We post nothing with a reply
    // handler (the native app's bridge is ReactNativeWebView.postMessage, which returns nothing).
    if (
      e.value === 'The WKWebView was deallocated before the message was delivered' &&
      frames.length === 0 &&
      /\bGSA\//.test(navigator.userAgent)
    ) {
      return null;
    }

    // The Instagram / Facebook iOS in-app browser's injected script redefining a read-only property
    // (SHOP-WEB-52). WebKit puts an injected script's frames on the page URL itself, line 1, column
    // ~7.8k: our HTML's first line is ~3.5k characters, and it calls no defineProperty anywhere.
    if (
      e.type === 'TypeError' &&
      e.value === 'Attempting to change value of a readonly property.' &&
      /\b(Instagram|FBAN|FBAV)\b/.test(navigator.userAgent) &&
      frames.some(isInlinePageFrame) &&
      frames.every((frame) => isInlinePageFrame(frame) || isNativeFrame(frame))
    ) {
      return null;
    }

    // Globals only the browsers' own injected scripts read: Firefox and Brave for iOS's reader mode
    // (`window.__firefox__`, SHOP-WEB-5M / -5N / -5P) and the Facebook iOS in-app browser
    // (`window._META.IABInnerFrame`, SHOP-WEB-5T), run where the global is not set up. WebKit puts an
    // injected script's frames on the page URL itself, line 1; our code reads neither global, and the
    // same message from a script file (ours or not) still reports.
    if (
      /\b__firefox__\b|\bwindow\._META\./.test(e.value ?? '') &&
      frames.length > 0 &&
      frames.every(isInlinePageFrame)
    ) {
      return null;
    }

    // Firefox's report of a `throw undefined` (SHOP-WEB-5K, Firefox for Android): no Error, so no stack
    // and nothing to point at; the SDK pins it on the page document. A throw of ours would be an Error.
    if (e.mechanism?.synthetic && e.value === 'uncaught exception: undefined' && frames.every(isInlinePageFrame)) {
      return null;
    }

    // Chrome terminating a listener as its frame is torn down (SHOP-WEB-53: our page inside a
    // WordPress "Content Mask" iframe). A string, not an Error, with no frames: the browser's report
    // that it stopped the code, not code that failed.
    if (
      e.mechanism?.synthetic &&
      frames.every((frame) => !isOwnFrame(frame)) &&
      /^event listener `simple event target \w+ listener` was terminated$/.test(e.value ?? '')
    ) {
      return null;
    }

    // Wallet and messaging browser extensions writing to globals of their own on our page.
    if (/window\.ethereum|runtime\.sendMessage\(\)/.test(e.value ?? '')) {
      return null;
    }

    // Safari's network-failure TypeError with no stack: nothing to point at.
    if (e.type === 'TypeError' && e.value === 'Load failed' && frames.length === 0) {
      return null;
    }

    if (
      e.type === 'SecurityError' &&
      e.value ===
        'Third-party iframes are not allowed to request payments unless explicitly allowed via Feature-Policy (payment)'
    ) {
      return null;
    }

    if (
      e.type === 'Error' &&
      ['Failed to load Stripe.js', 'Could not load "places_impl".', 'Could not load "util".'].includes(e.value)
    ) {
      return null;
    }

    // Safari's own JSON-LD reader, injected into the page, crashing on a value in our structured
    // data (FOODAMIGOS-SHOP-WEB-29C: `hasOwnProperty.call(o,"telephone")` on null). MetaInfo no longer
    // emits nulls (see StructuredData), so this only catches whatever else that reader trips over.
    // Sentry's `ignoreErrors` matches the message, never frame names, so this has to live here.
    if (frames.some((frame) => /^extractSchemaValuesFrom(JSONLD|SchemaOrg)$/.test(frame.function ?? ''))) {
      return null;
    }

    // An extension's or injected script's own error, e.g. `zp_token is not defined` (SHOP-WEB-3B).
    if (isInjectedStack(frames, e.mechanism?.type)) {
      return null;
    }

    if (FOREIGN_ERROR_MESSAGE.test(e.value ?? '')) {
      return null;
    }

    // Cross-origin frame access: an embedding page or an extension reaching into another frame.
    if (
      e.type === 'SecurityError' &&
      /Blocked a frame with origin|Failed to read a named property 'document' from 'Window'/.test(e.value ?? '')
    ) {
      return null;
    }

    // Nothing of ours aborts a request without a stack, so this is the browser cancelling an
    // in-flight fetch as the visitor leaves the page.
    if (e.type === 'AbortError' && frames.length === 0) {
      return null;
    }

    // Frames run oldest to newest, so the last one threw. When an injected script runs from a listener,
    // Sentry's own addEventListener wrapper (minified into our bundle) sits at the bottom of the stack,
    // so "no frame of ours" is too strict for these; "not thrown by our code" is the test.
    const thrownOutsideOurBundle = frames.length === 0 || !isOwnFrame(frames[frames.length - 1]);

    // Android WebView shells (browser apps wrapping the page) inject a script that calls their own
    // `xbrowser` / `swbrowser` bridge object (SHOP-WEB-2G / -2H / -2J). Its frames are `<anonymous>`
    // (`needInjectCss`, `execute_auto_fill`); the same ReferenceError thrown by our bundle still comes through.
    if (
      e.type === 'ReferenceError' &&
      /^(?:x|sw)browser is not defined$/.test(e.value ?? '') &&
      thrownOutsideOurBundle
    ) {
      return null;
    }

    // The Facebook / Instagram in-app browser's own script, inlined into the page, posting page-hide
    // data to an iOS bridge that is not there (SHOP-WEB-14 / -1P / -2T). Its frames sit on the page
    // URL itself (`/athen:1:6257`, `https://….online-karte.com/:1:…`), never on a script file, and the
    // unminified build names them `sendDataToNative` / `sendPageHideMessage`. We never use
    // `window.webkit.messageHandlers`, and our one inline script (the gtag stub) does not either; the
    // frame shape is what has to match, so a future bridge of ours failing the same way still reports.
    // Another in-app browser hooks `console` and forwards each call to its bridge (SHOP-WEB-5J): our
    // bundle logs (Amplitude's "Load failed"), and the hook throws from a frame WebKit reports only as
    // `[native code]`. A call of ours would leave its own frame on top, so that frame is the tell.
    if (
      e.type === 'TypeError' &&
      /window\.webkit\.messageHandlers/.test(e.value ?? '') &&
      thrownOutsideOurBundle &&
      (frames.some((frame) => ['sendDataToNative', 'sendPageHideMessage'].includes(frame.function ?? '')) ||
        (frames.length > 0 && frames.every(isInlinePageFrame)) ||
        frames[frames.length - 1]?.filename === '[native code]')
    ) {
      return null;
    }

    // A stack overflow is ours only when our own code recursed (see isRecursionInOurCode).
    // Chrome for iOS and the Google iOS app inject content scripts (translate, autofill, link
    // preview) that walk the DOM recursively and blow WebKit's small JS stack on long menu pages;
    // WebKit attributes those frames to the document or to nothing, never to /assets/*.js.
    // Firefox runs out of stack inside a plain dayjs/moment parse on a shallow stack with nothing
    // recursive on it (SHOP-WEB-5D / -6Q), and reports it as `InternalError: too much recursion`.
    if (/Maximum call stack size exceeded|too much recursion/.test(e.value ?? '') && !isRecursionInOurCode(frames)) {
      return null;
    }
  }

  nameCapturedMessage(event);

  return event;
}

/**
 * With `attachStacktrace`, the SDK puts a captureMessage's stack on an exception with no type, and
 * Sentry then titles the issue after the minified top frame ("Rt" for SHOP-WEB-5E, "q" for
 * SHOP-WEB-4). Typing it gives the issue the message as its title. Every captureMessage of ours
 * sets its own fingerprint, so grouping does not change.
 */
function nameCapturedMessage(event: ErrorEvent): void {
  const values = event.exception?.values;

  if (typeof event.message !== 'string' || values?.length !== 1) {
    return;
  }

  const [value] = values;

  if (!value.type && value.value === event.message) {
    value.type = 'Message';
  }
}
